import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { MenuItem } from '@src/common/components/Menu';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { CancelPermitApplicationModal } from '@src/orders/components/modal/CancelPermitApplicationModal';
import {
  actionIconStyle,
  menuItemStyle,
} from '@src/orders/components/permitList/PermitActions/PermitActionsStyles';
import type { OrderListing } from '@src/orders/domain/OrderList';
import type { PermitSummary } from '@src/orders/domain/PermitSummary';
import { useCancelDraftPermit } from '@src/orders/hooks/useCancelDraftPermit';
import { useRefreshOrders } from '@src/orders/hooks/useRefreshOrders';
import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends ComponentPropsWithoutRef<typeof MenuItem> {
  permit: PermitSummary;
  order: OrderListing;
}
export const CancelPermit = forwardRef<HTMLLIElement, Props>(
  ({ permit, order, ...menuItemProps }, ref) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { refreshOrders } = useRefreshOrders();
    const cancelPermitCallbacks = {
      onSuccess: () => {
        showToast({
          severity: ToastLevel.success,
          message: t('permitSummaries.cancelPermit.success'),
        });
        refreshOrders(order.id);
        setCancelModal(false);
      },
      onError: () =>
        showToast({
          severity: ToastLevel.error,
          message: t('permitSummaries.cancelPermit.error'),
        }),
    };

    const { cancelDraftPermit } = useCancelDraftPermit({
      orderId: order.id,
      onError: cancelPermitCallbacks.onError,
      onSuccess: cancelPermitCallbacks.onSuccess,
    });

    const [permitToCancel, setPermitToCancel] = useState<
      PermitSummary | undefined
    >(undefined);
    const [openCancelModal, setCancelModal] = useState(false);

    const handleCancelPermit = () => {
      if (order.status === 'DRAFT') {
        cancelDraftPermit({ permitApplicationId: permit.id });
        return;
      }
      setPermitToCancel(permit);
      setCancelModal(true);
    };

    return (
      <>
        <MenuItem
          {...menuItemProps}
          ref={ref}
          className={menuItemStyle}
          style={{ color: theme.palette.error.error }}
          disabled={!order.isActive}
          onClick={() => handleCancelPermit()}
        >
          <CancelOutlinedIcon style={actionIconStyle} />
          {t('cancel')}
        </MenuItem>
        {permitToCancel && (
          <CancelPermitApplicationModal
            open={openCancelModal}
            onClose={() => {
              setCancelModal(false);
            }}
            onSuccess={cancelPermitCallbacks.onSuccess}
            onError={cancelPermitCallbacks.onError}
            orderId={order.id}
            permit={permitToCancel}
          />
        )}
      </>
    );
  },
);
